import type { CSSProperties, JSX } from "react";

import { CITY_GUIDE_EXPERIENCE_LIMIT } from "@noko-city/shared";

import { Reveal } from "../../Reveal";
import type { SectionRenderInput } from "./types";

/**
 * UNIQUE EXPERIENCES.
 *
 * The section carries no authored illustration: the version that shared the row
 * with one held the cards in half the width and read as two unrelated columns, so
 * the tiles divide the column themselves now. The row wrapper goes with the
 * illustration — the list is the section's only child.
 *
 * The places come from the city's curated pool — the order it was curated in, set
 * in City settings → Information → Attractions — and are resolved into cards at
 * read time, so the guide stores no titles or blurbs of its own. Nothing is printed
 * under the blurbs: neither the reading nor the photographer credit that used to be
 * here, and `wikipedia-blurb.ts` is the note that records it.
 *
 * One column per place, five at most, so a two-place city fills its row and a
 * five-place city does not leave one tile orphaned on a second row.
 */
export function ExperiencesSection({ section, page }: SectionRenderInput): JSX.Element {
  const cards = (page.attractions ?? []).slice(0, section.limit ?? CITY_GUIDE_EXPERIENCE_LIMIT);
  const columns = Math.max(1, Math.min(cards.length, 5));
  return (
    <ul
      className="atlas-guide-experiences"
      style={{ "--experience-cols": columns } as CSSProperties}
    >
      {cards.map((card, position) => (
        <li key={`${card.name}-${position}`}>
          <Reveal variant="rise" delay={position * 60}>
            <article className="atlas-guide-tile atlas-guide-experience">
              {card.image && (
                <div className="atlas-guide-experience-image">
                  <img
                    src={card.image}
                    srcSet={card.imageSrcSet ?? undefined}
                    // The grid is two columns under 768 px however many cards there
                    // are, and one column per card above it, so this is the card's
                    // own share of the window. The stylesheet's own widths stay the
                    // authority: an over-stated share costs a larger file, an
                    // under-stated one costs sharpness.
                    sizes={`(max-width: 767px) 50vw, ${Math.ceil(100 / columns)}vw`}
                    alt={card.name}
                    loading="lazy"
                    decoding="async"
                  />
                </div>
              )}
              <h3 className="atlas-guide-tile-title">{card.name}</h3>
              {card.blurb && <p className="atlas-guide-tile-body">{card.blurb}</p>}
            </article>
          </Reveal>
        </li>
      ))}
    </ul>
  );
}
